import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Button,
  Chip,
  Card,
  CardContent,
  Grid,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Paper,
  ImageList,
  ImageListItem,
  Dialog,
  DialogContent,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  CloudUpload,
  CheckCircle,
  ErrorOutline,
  Description,
  Download,
  Delete,
  Save,
  Cancel,
  Info,
  Folder,
  ShowChart,
  Sensors,
  PhotoLibrary,
  VideoLibrary,
  AddPhotoAlternate,
  PlayArrow,
  Close,
  Edit,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout';
import { CorporateTitle } from '../components/ui/CorporateTitle';
import { CorporateButton } from '../components/ui/CorporateButton';
import { CorporateDialog } from '../components/ui/CorporateDialog';
import { CorporateRichTextEditor } from '../components/ui/CorporateRichTextEditor';
import { CorporateTextField } from '../components/ui/CorporateTextField';
import { useToast } from '../contexts/ToastContext';
import { apiService } from '../services/api';
import {
  buildSensorDataCacheKey,
  getCachedSensorData,
  setCachedSensorData,
  invalidateEnsayoSensorCache,
} from '../utils/sensorDataCache';
import { Ensayo, EnsayoMedia, EnsayoMediaList, ActuadorData, Sensor, SensorData } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { VisualizadorDatosEnsayo } from '../components/visualizacion/VisualizadorDatosEnsayo';
import { SensorMetadata } from '../components/SensorMetadata';
import { NomenclaturaDocLink } from '../components/documents/NomenclaturaDocLink';
import { ArchivosSensoresHint } from '../components/ensayos/ArchivosSensoresHint';
import { inferSensoresFileFormat } from '../utils/inferSensoresFileFormat';

/**
 * Wrapper para usar Tab dentro de Tooltip. Filtra props de Tabs (indicatorColor,
 * selectionFollowsFocus, indicator) que provocan warnings en el DOM, y reenvía
 * el resto al Tab interno para que la selección funcione (onClick, value, etc.)
 */
const TabTooltipWrapper = React.forwardRef<HTMLSpanElement, any>((props, ref) => {
  const {
    children,
    indicatorColor: _ic,
    selectionFollowsFocus: _sff,
    indicator: _ind,
    ...rest
  } = props;

  return (
    <span ref={ref} style={{ display: 'inline-block' }}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, rest);
        }
        return child;
      })}
    </span>
  );
});

export const EnsayoDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('MANAGE_ENSAYOS');
  const canEditFiles = true; // Allow all authenticated users to manage files (upload/delete/process)

  const [ensayo, setEnsayo] = useState<Ensayo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingActuador, setIsUploadingActuador] = useState(false);
  const [isUploadingSensores, setIsUploadingSensores] = useState(false);
  const [isDownloadingActuador, setIsDownloadingActuador] = useState(false);
  const [isDownloadingSensores, setIsDownloadingSensores] = useState(false);
  const [isDeletingActuador, setIsDeletingActuador] = useState(false);
  const [isDeletingSensores, setIsDeletingSensores] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [actuadorData, setActuadorData] = useState<ActuadorData[]>([]);
  const [actuadorDataTotal, setActuadorDataTotal] = useState<number>(0);
  const [isLoadingActuadorData, setIsLoadingActuadorData] = useState(false);
  const [isProcessingActuador, setIsProcessingActuador] = useState(false);
  const [actuadorDataError, setActuadorDataError] = useState<string | null>(null);
  
  // Estados para sensores
  const [sensores, setSensores] = useState<Sensor[]>([]);
  const [datosPorSensor, setDatosPorSensor] = useState<Record<string, SensorData[]>>({});
  const [isLoadingSensores, setIsLoadingSensores] = useState(false);
  const [isLoadingSensorData, setIsLoadingSensorData] = useState(false);
  const [isProcessingSensores, setIsProcessingSensores] = useState(false);
  const [sensoresError, setSensoresError] = useState<string | null>(null);
  
  // Estados para edición
  const [isEditingNombre, setIsEditingNombre] = useState(false);
  const [isEditingCodigo, setIsEditingCodigo] = useState(false);
  const [isEditingFecha, setIsEditingFecha] = useState(false);
  const [isEditingObjetivos, setIsEditingObjetivos] = useState(false);
  const [isEditingIncidencias, setIsEditingIncidencias] = useState(false);
  const [isEditingConclusiones, setIsEditingConclusiones] = useState(false);
  const [editNombre, setEditNombre] = useState('');
  const [editCodigo, setEditCodigo] = useState('');
  const [editFecha, setEditFecha] = useState('');
  const [editObjetivos, setEditObjetivos] = useState('');
  const [editIncidencias, setEditIncidencias] = useState('');
  const [editConclusiones, setEditConclusiones] = useState('');
  
  // Estados para modal de edición de texto
  const [textEditModalOpen, setTextEditModalOpen] = useState(false);
  const [textEditField, setTextEditField] = useState<'objetivos' | 'incidencias' | 'conclusiones' | null>(null);
  const [textEditValue, setTextEditValue] = useState('');
  
  // Estado para pestañas
  const [activeTab, setActiveTab] = useState(0);
  
  // Estados para carga lazy de datos
  const [hasLoadedActuadorData, setHasLoadedActuadorData] = useState(false);
  const [hasLoadedSensores, setHasLoadedSensores] = useState(false);
  const [hasLoadedSensorData, setHasLoadedSensorData] = useState(false);
  
  // Estados para galería
  const [galleryMedia, setGalleryMedia] = useState<EnsayoMediaList>({ images: [], videos: [] });
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [isDownloadingMedia, setIsDownloadingMedia] = useState(false);
  const [isDownloadingAllMedia, setIsDownloadingAllMedia] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number>(0);
  const [selectedMediaType, setSelectedMediaType] = useState<'image' | 'video'>('image');
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [editingDescription, setEditingDescription] = useState<string | null>(null);
  const [editDescriptionValue, setEditDescriptionValue] = useState<string>('');
  const [videoThumbnails, setVideoThumbnails] = useState<Record<string, string>>({});
  // Blob URLs para archivos multimedia (para asegurar que se envíen las cookies)
  const [mediaBlobUrls, setMediaBlobUrls] = useState<Record<string, string>>({});
  const mediaBlobUrlsRef = useRef<Record<string, string>>({});
  
  // Estado para diálogo de confirmación de eliminación
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<'actuador' | 'sensores' | null>(null);
  
  // Estado para diálogo de confirmación de eliminación de media
  const [deleteMediaDialogOpen, setDeleteMediaDialogOpen] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<string | null>(null);
  const [isDeletingMedia, setIsDeletingMedia] = useState(false);
  
  const actuadorFileInputRef = useRef<HTMLInputElement>(null);
  const sensoresFileInputRef = useRef<HTMLInputElement>(null);
  const mediaFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (id) {
      loadEnsayo();
    }
  }, [id]);

  // Cargar media cuando se accede a la pestaña de información
  useEffect(() => {
    if (activeTab === 0 && id) {
      loadMedia();
    }
  }, [activeTab, id]);
  
  // Polling para verificar estado de procesamiento
  useEffect(() => {
    if (!ensayo || !id) return;
    
    // Si hay algún archivo en estado PENDING, iniciar polling
    const isActuadorPending = ensayo.actuador_processing_status === 'PENDING';
    const isSensoresPending = ensayo.sensores_processing_status === 'PENDING';
    
    if (isActuadorPending || isSensoresPending) {
      const timer = setInterval(() => {
        loadEnsayo(false);
      }, 3000); // Poll every 3 seconds
      return () => clearInterval(timer);
    }
  }, [ensayo, id]);

  // Actualizar descripción cuando cambia el media seleccionado en el modal
  useEffect(() => {
    if (mediaDialogOpen) {
      const allMedia = [...galleryMedia.images, ...galleryMedia.videos];
      const currentMedia = allMedia[selectedMediaIndex];
      if (currentMedia && editingDescription !== currentMedia.id) {
        setEditDescriptionValue(currentMedia.description || '');
      }
    }
  }, [selectedMediaIndex, mediaDialogOpen, galleryMedia]);

  // Validar estado de datos para determinar qué pestañas están disponibles
  const getTabValidation = () => {
    if (!ensayo) {
      return { 
        info: { available: false, hasError: true },
        files: { available: false, hasError: false },
        visualizador: { available: false, hasError: false },
        sensores: { available: false, hasError: false },
      };
    }
    
    const hasInfo = Boolean(ensayo.objetivos && ensayo.objetivos.trim() !== '');
    // Verificar tanto el archivo como el nombre del archivo para mayor robustez
    const hasActuadorFile = Boolean(ensayo.actuador_file || ensayo.actuador_file_name);
    const hasSensoresFile = Boolean(ensayo.sensores_file || ensayo.sensores_file_name);
    
    // Visualizador está disponible si hay archivo de actuador O sensores
    const visualizadorAvailable = hasActuadorFile || hasSensoresFile;
    // Sensores está disponible solo si hay archivo de sensores
    const sensoresAvailable = hasSensoresFile;
    // Sensores tiene error solo si se accedió a la pestaña, se intentó cargar y no hay datos procesados
    const sensoresHasError = activeTab === 2 && 
                             sensoresAvailable && 
                             hasLoadedSensores && 
                             !isLoadingSensores && 
                             sensores.length === 0 &&
                             !sensoresError; // No mostrar error si hay un error de carga real
    
    // Visualizador tiene error solo si se accedió a la pestaña, se intentó cargar y no hay datos procesados
    // Mostrar error solo cuando:
    // 1. Estamos en la pestaña de visualizador (ahora índice 3)
    // 2. Hay archivo de actuador o sensores
    // 3. Se intentó cargar (hasLoadedActuadorData o hasLoadedSensores)
    // 4. Ya terminó de cargar (!isLoadingActuadorData y !isLoadingSensores)
    // 5. No hay datos (actuadorData.length === 0 y sensores.length === 0)
    const visualizadorHasError = activeTab === 3 && 
                                  visualizadorAvailable && 
                                  ((hasActuadorFile && hasLoadedActuadorData && !isLoadingActuadorData && actuadorData.length === 0 && !actuadorDataError) ||
                                   (hasSensoresFile && hasLoadedSensores && !isLoadingSensores && sensores.length === 0 && !sensoresError));
    
    return {
      info: { 
        available: true, 
        hasError: !hasInfo 
      },
      files: { 
        available: true, 
        hasError: false 
      },
      visualizador: { 
        available: visualizadorAvailable, 
        hasError: visualizadorHasError 
      },
      sensores: { 
        available: sensoresAvailable, 
        hasError: sensoresHasError 
      },
    };
  };

  // Calcular validación de pestañas
  const tabValidation = getTabValidation();

  const loadEnsayo = async (showLoading = true) => {
    if (!id) return;
    if (showLoading) {
      setIsLoading(true);
    }
    try {
      const data = await apiService.getEnsayo(id);
      // DEBUG: Log para identificar el problema de status
      console.log('[EnsayoDetailPage] Ensayo loaded:', { 
        id: data.id, 
        codigo: data.codigo,
        displacement_calculation_status: data.displacement_calculation_status,
        fos_strain_calculation_status: data.fos_strain_calculation_status 
      });
      setEnsayo(data);
      // Inicializar valores de edición
      setEditNombre(data.nombre || '');
      setEditCodigo(data.codigo || '');
      setEditFecha(data.fecha_ensayo || '');
      setEditObjetivos(data.objetivos || '');
      setEditIncidencias(data.incidencias || '');
      setEditConclusiones(data.conclusiones || '');
      
      // NO cargar datos automáticamente - se cargarán bajo demanda cuando se acceda a la pestaña
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.loadError', 'No se pudo cargar el ensayo'));
      navigate('/ensayos');
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  const loadMedia = async () => {
    if (!id) return;
    setIsLoadingMedia(true);
    try {
      const data = await apiService.getEnsayoMedia(id);
      setGalleryMedia(data);
      
      // Limpiar blob URLs anteriores
      Object.values(mediaBlobUrls).forEach(url => URL.revokeObjectURL(url));
      const newBlobUrls: Record<string, string> = {};
      
      // Cargar todos los archivos como blobs para asegurar que se envíen las cookies
      const allMedia = [...data.images, ...data.videos];
      for (const media of allMedia) {
        try {
          const blob = await apiService.getMediaFileAsBlob(id, media.id);
          const blobUrl = URL.createObjectURL(blob);
          newBlobUrls[media.id] = blobUrl;
        } catch (err) {
          console.error(`Error al cargar blob para media ${media.id}:`, err);
        }
      }
      
      setMediaBlobUrls(newBlobUrls);
      mediaBlobUrlsRef.current = newBlobUrls;
    } catch (err: any) {
      console.error('Error al cargar media:', err);
      setGalleryMedia({ images: [], videos: [] });
    } finally {
      setIsLoadingMedia(false);
    }
  };
  
  // Limpiar blob URLs cuando el componente se desmonte
  useEffect(() => {
    return () => {
      Object.values(mediaBlobUrlsRef.current).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const handleMediaSelect = () => {
    mediaFileInputRef.current?.click();
  };

  const handleMediaChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0 || !id) return;

    // Validar tipos de archivo
    const validImageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
    const validVideoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
    
    const validFiles: File[] = [];
    for (const file of files) {
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (validImageExtensions.includes(fileExtension) || validVideoExtensions.includes(fileExtension)) {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) {
      showError(t('ensayos.invalidMediaFormat', 'Formato de archivo no válido. Use imágenes (jpg, png, gif, webp) o videos (mp4, webm, mov)'));
      return;
    }

    try {
      setIsUploadingMedia(true);
      const uploaded = await apiService.uploadMediaFiles(id, validFiles);
      showSuccess(t('ensayos.mediaUploadSuccess', `${uploaded.length} archivo(s) subido(s) correctamente`));
      await loadMedia();
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.uploadError', 'Error al subir los archivos'));
    } finally {
      setIsUploadingMedia(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleStartEditDescription = (mediaId: string, currentDescription: string | null) => {
    setEditingDescription(mediaId);
    setEditDescriptionValue(currentDescription || '');
  };

  const handleCancelEditDescription = () => {
    setEditingDescription(null);
    setEditDescriptionValue('');
  };

  const handleSaveDescription = async (mediaId: string) => {
    if (!id) return;
    try {
      await apiService.updateMediaDescription(id, mediaId, editDescriptionValue || null);
      showSuccess(t('ensayos.descriptionUpdated', 'Descripción actualizada correctamente'));
      setEditingDescription(null);
      await loadMedia();
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.updateError', 'Error al actualizar la descripción'));
    }
  };

  // Generar thumbnail para videos
  const generateVideoThumbnail = (videoUrl: string, mediaId: string) => {
    if (videoThumbnails[mediaId]) return; // Ya existe
    
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = videoUrl;
    video.currentTime = 1; // Tomar frame en el segundo 1
    
    video.addEventListener('loadeddata', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnailUrl = canvas.toDataURL('image/jpeg');
        setVideoThumbnails(prev => ({ ...prev, [mediaId]: thumbnailUrl }));
      }
    });
    
    video.load();
  };

  const handleOpenMedia = (media: EnsayoMedia, type: 'image' | 'video') => {
    const allMedia = [...galleryMedia.images, ...galleryMedia.videos];
    const index = allMedia.findIndex(m => m.id === media.id);
    if (index !== -1) {
      setSelectedMediaIndex(index);
      setSelectedMediaType(type);
      setMediaDialogOpen(true);
      // Inicializar estado de edición con la descripción actual
      setEditingDescription(null);
      setEditDescriptionValue(media.description || '');
    }
  };

  const handlePreviousMedia = () => {
    const allMedia = [...galleryMedia.images, ...galleryMedia.videos];
    if (allMedia.length === 0) return;
    const newIndex = selectedMediaIndex > 0 ? selectedMediaIndex - 1 : allMedia.length - 1;
    setSelectedMediaIndex(newIndex);
    const media = allMedia[newIndex];
    setSelectedMediaType(media.file_type);
    // Actualizar estado de edición con la descripción del nuevo media
    setEditingDescription(null);
    setEditDescriptionValue(media.description || '');
  };

  const handleNextMedia = () => {
    const allMedia = [...galleryMedia.images, ...galleryMedia.videos];
    if (allMedia.length === 0) return;
    const newIndex = selectedMediaIndex < allMedia.length - 1 ? selectedMediaIndex + 1 : 0;
    setSelectedMediaIndex(newIndex);
    const media = allMedia[newIndex];
    setSelectedMediaType(media.file_type);
    // Actualizar estado de edición con la descripción del nuevo media
    setEditingDescription(null);
    setEditDescriptionValue(media.description || '');
  };

  const handleDeleteMedia = async (mediaId: string) => {
    if (!id) return;
    try {
      setIsDeletingMedia(true);
      await apiService.deleteMediaFile(id, mediaId);
      
      // Limpiar blob URL del archivo eliminado
      if (mediaBlobUrls[mediaId]) {
        URL.revokeObjectURL(mediaBlobUrls[mediaId]);
        const newBlobUrls = { ...mediaBlobUrls };
        delete newBlobUrls[mediaId];
        setMediaBlobUrls(newBlobUrls);
        mediaBlobUrlsRef.current = newBlobUrls;
      }
      
      showSuccess(t('ensayos.mediaDeleteSuccess', 'Archivo multimedia eliminado correctamente'));
      await loadMedia();
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.deleteError', 'Error al eliminar el archivo'));
    } finally {
      setIsDeletingMedia(false);
    }
  };

  const handleRequestDeleteMedia = (mediaId: string) => {
    setMediaToDelete(mediaId);
    setDeleteMediaDialogOpen(true);
  };

  const handleConfirmDeleteMedia = async () => {
    if (mediaToDelete) {
      await handleDeleteMedia(mediaToDelete);
      setDeleteMediaDialogOpen(false);
      setMediaToDelete(null);
    }
  };

  const handleDownloadMedia = async (media: EnsayoMedia) => {
    if (!id) return;
    try {
      setIsDownloadingMedia(true);
      const blob = await apiService.getMediaFileAsBlob(id, media.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = media.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess(t('ensayos.mediaDownloadSuccess', 'Archivo descargado correctamente'));
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.downloadError', 'Error al descargar el archivo'));
    } finally {
      setIsDownloadingMedia(false);
    }
  };

  const handleDownloadAllMedia = async () => {
    if (!id) return;
    const allMedia = [...galleryMedia.images, ...galleryMedia.videos];
    if (allMedia.length === 0) {
      showError(t('ensayos.noMediaToDownload', 'No hay archivos para descargar'));
      return;
    }

    try {
      setIsDownloadingAllMedia(true);
      
      // Descargar todos los archivos uno por uno
      for (const media of allMedia) {
        try {
          const blob = await apiService.getMediaFileAsBlob(id, media.id);
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = media.file_name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          // Pequeña pausa entre descargas para evitar problemas del navegador
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (err) {
          console.error(`Error al descargar ${media.file_name}:`, err);
        }
      }
      
      showSuccess(t('ensayos.allMediaDownloadSuccess', 'Todos los archivos se están descargando'));
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.downloadError', 'Error al descargar los archivos'));
    } finally {
      setIsDownloadingAllMedia(false);
    }
  };

  // Cargar datos cuando se accede a una pestaña específica
  useEffect(() => {
    if (!ensayo || !id) return;
    
    // Cargar sensores cuando se accede a Archivos (1), Sensores (2) o Visualizador (3)
    if ((activeTab === 1 || activeTab === 2 || activeTab === 3) && ensayo.sensores_file && !hasLoadedSensores) {
      loadSensores();
      setHasLoadedSensores(true);
    }
    
    // Cargar datos del actuador cuando se accede al Visualizador (índice 3)
    if (activeTab === 3) {
      if (ensayo.actuador_file && !hasLoadedActuadorData) {
        loadActuadorData();
        setHasLoadedActuadorData(true);
      }
    }
  }, [activeTab, ensayo, id, hasLoadedActuadorData, hasLoadedSensores]);

  // Recalcular validación cuando cambian los datos cargados o el estado de carga
  useEffect(() => {
    // Esto fuerza la recalculación de la validación cuando cambian los datos
    // La validación se recalcula automáticamente en cada render porque getTabValidation() se llama en el render
  }, [actuadorData.length, sensores.length, hasLoadedActuadorData, hasLoadedSensores, isLoadingActuadorData, isLoadingSensores, activeTab]);

  const [movingAverageWindow, setMovingAverageWindow] = useState<number | null>(null);

  const loadActuadorData = useCallback(async (movingAvgWindow?: number | null) => {
    if (!id) return;
    setIsLoadingActuadorData(true);
    setActuadorDataError(null);
    try {
      const response = await apiService.getActuadorData(
        id,
        2000, // maxPoints
        movingAvgWindow !== null && movingAvgWindow !== undefined ? movingAvgWindow : undefined
      );
      setActuadorData(response.data);
      setActuadorDataTotal(response.total || response.data.length);
    } catch (err: any) {
      // Si no hay datos, no es un error crítico
      if (err.response?.status === 404 || err.response?.status === 400) {
        setActuadorDataError(null);
        setActuadorData([]);
        setActuadorDataTotal(0);
      } else {
        setActuadorDataError(err.response?.data?.detail || 'Error al cargar los datos del actuador');
      }
    } finally {
      setIsLoadingActuadorData(false);
    }
  }, [id]);

  const DISPLAY_MAX_POINTS = 2000;

  const handleLoadSensorData = useCallback(async (sensorId: string, movingAvgWindow?: number, useAdjustedTimestamps?: boolean) => {
    if (!id) return;
    const cacheKey = buildSensorDataCacheKey(
      id,
      sensorId,
      DISPLAY_MAX_POINTS,
      movingAvgWindow,
      useAdjustedTimestamps
    );
    const cached = getCachedSensorData(cacheKey);
    if (cached) {
      setDatosPorSensor((prev) => ({ ...prev, [sensorId]: cached }));
      return;
    }
    try {
      const response = await apiService.getSensorData(
        id,
        sensorId,
        undefined,
        undefined,
        DISPLAY_MAX_POINTS,
        movingAvgWindow !== null && movingAvgWindow !== undefined ? movingAvgWindow : undefined,
        useAdjustedTimestamps
      );
      setCachedSensorData(cacheKey, response.data || [], response.total);
      setDatosPorSensor((prev) => ({
        ...prev,
        [sensorId]: response.data || [],
      }));
    } catch (err: any) {
      console.error(`Error al cargar datos del sensor ${sensorId}:`, err);
      setDatosPorSensor((prev) => ({
        ...prev,
        [sensorId]: [],
      }));
    }
  }, [id]);

  const handleLoadSensorsBatch = useCallback(async (
    sensorIds: string[],
    movingAvgWindow?: number,
    useAdjustedTimestamps?: boolean
  ) => {
    if (!id || sensorIds.length === 0) return;

    const uncachedIds: string[] = [];
    const cachedUpdates: Record<string, SensorData[]> = {};

    sensorIds.forEach((sensorId) => {
      const cacheKey = buildSensorDataCacheKey(
        id,
        sensorId,
        DISPLAY_MAX_POINTS,
        movingAvgWindow,
        useAdjustedTimestamps
      );
      const cached = getCachedSensorData(cacheKey);
      if (cached) {
        cachedUpdates[sensorId] = cached;
      } else {
        uncachedIds.push(sensorId);
      }
    });

    if (Object.keys(cachedUpdates).length > 0) {
      setDatosPorSensor((prev) => ({ ...prev, ...cachedUpdates }));
    }

    if (uncachedIds.length === 0) return;

    try {
      const batch = await apiService.getSensorDataBatch(id, uncachedIds, {
        maxPoints: DISPLAY_MAX_POINTS,
        movingAverageWindow:
          movingAvgWindow !== null && movingAvgWindow !== undefined ? movingAvgWindow : undefined,
        useAdjustedTimestamps,
      });
      const updates: Record<string, SensorData[]> = {};
      Object.entries(batch.results).forEach(([sensorId, payload]) => {
        const data = payload.data || [];
        updates[sensorId] = data;
        const cacheKey = buildSensorDataCacheKey(
          id,
          sensorId,
          DISPLAY_MAX_POINTS,
          movingAvgWindow,
          useAdjustedTimestamps
        );
        setCachedSensorData(cacheKey, data, payload.total);
      });
      if (Object.keys(updates).length > 0) {
        setDatosPorSensor((prev) => ({ ...prev, ...updates }));
      }
      Object.entries(batch.errors).forEach(([sensorId, message]) => {
        console.error(`Error batch sensor ${sensorId}:`, message);
        setDatosPorSensor((prev) => ({ ...prev, [sensorId]: [] }));
      });
    } catch (err) {
      console.error('Error en carga batch de sensores:', err);
      await Promise.all(
        uncachedIds.map((sensorId) =>
          handleLoadSensorData(sensorId, movingAvgWindow, useAdjustedTimestamps)
        )
      );
    }
  }, [id, handleLoadSensorData]);

  const handleMovingAverageChange = useCallback(async (window: number | null, useAdjustedTimestamps?: boolean) => {
    setMovingAverageWindow(window);
    invalidateEnsayoSensorCache(id || '');
    await loadActuadorData(window);
    setDatosPorSensor((prev) => {
      const sensorIds = Object.keys(prev);
      if (sensorIds.length > 0) {
        void handleLoadSensorsBatch(
          sensorIds,
          window !== null && window !== undefined ? window : undefined,
          useAdjustedTimestamps
        );
      }
      return prev;
    });
  }, [id, loadActuadorData, handleLoadSensorsBatch]);

  const handleProcessActuador = async () => {
    if (!id) return;
    setIsProcessingActuador(true);
    try {
      await apiService.processActuadorFile(id);
      showSuccess(t('ensayos.processSuccess', 'Archivo procesado correctamente'));
      await loadActuadorData();
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.processError', 'Error al procesar el archivo'));
    } finally {
      setIsProcessingActuador(false);
    }
  };

  const loadSensores = useCallback(async (silent = false) => {
    if (!id) return;
    if (!silent) setIsLoadingSensores(true);
    setSensoresError(null);
    try {
      const response = await apiService.getSensores(id);
      setSensores(response.sensores || []);
      
      // NO cargar datos automáticamente - se cargarán bajo demanda
    } catch (err: any) {
      if (err.response?.status === 404 || err.response?.status === 400) {
        setSensoresError(null);
        setSensores([]);
      } else {
        setSensoresError(err.response?.data?.detail || 'Error al cargar los sensores');
      }
    } finally {
      if (!silent) setIsLoadingSensores(false);
    }
  }, [id]);

  const loadSensorData = async (sensoresList: Sensor[]) => {
    if (!id) return;
    setIsLoadingSensorData(true);
    try {
      const datosMap: Record<string, SensorData[]> = {};
      
      // NO cargar datos automáticamente de todos los sensores
      // Los datos se cargarán bajo demanda cuando el usuario seleccione un sensor para visualizar
      // Esto evita cargar 245000+ registros al abrir la página
      // Similar a como funciona con el actuador, que solo carga cuando se expande la sección
      
      setDatosPorSensor(datosMap);
    } catch (err: any) {
      console.error('Error al cargar datos de sensores:', err);
      setDatosPorSensor({});
    } finally {
      setIsLoadingSensorData(false);
    }
  };

  const handleProcessSensores = async () => {
    if (!id) return;
    setIsProcessingSensores(true);
    try {
      await apiService.processSensoresFile(id);
      showSuccess(t('ensayos.processSuccess', 'Archivo procesado correctamente'));
      await loadSensores();
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.processError', 'Error al procesar el archivo'));
    } finally {
      setIsProcessingSensores(false);
    }
  };

  const handleFileSelect = (type: 'actuador' | 'sensores') => {
    if (type === 'actuador') {
      actuadorFileInputRef.current?.click();
    } else {
      sensoresFileInputRef.current?.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, type: 'actuador' | 'sensores') => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    // Validar extensión
    const validExtensions = ['.xlsx', '.xls'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      showError(t('ensayos.invalidFileFormat', 'Formato de archivo no válido. Use .xlsx o .xls'));
      return;
    }

    try {
      if (type === 'actuador') {
        setIsUploadingActuador(true);
        const updated = await apiService.uploadActuadorFile(id, file);
        setEnsayo(updated);
        // Recargar el ensayo para asegurar sincronización completa (sin mostrar spinner)
        await loadEnsayo(false);
        showSuccess(t('ensayos.actuadorUploadSuccess', 'Archivo de actuador subido correctamente'));
        // Resetear flag para que se cargue cuando se acceda a la pestaña
        setHasLoadedActuadorData(false);
      } else {
        setIsUploadingSensores(true);
        const updated = await apiService.uploadSensoresFile(id, file);
        setEnsayo(updated);
        // Recargar el ensayo para asegurar sincronización completa (sin mostrar spinner)
        await loadEnsayo(false);
        showSuccess(t('ensayos.sensoresUploadSuccess', 'Archivo de sensores subido correctamente'));
        // Resetear flag para que se cargue cuando se acceda a la pestaña
        setHasLoadedSensores(false);
      }
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.uploadError', 'Error al subir el archivo'));
    } finally {
      setIsUploadingActuador(false);
      setIsUploadingSensores(false);
      // Limpiar el input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleDownload = async (type: 'actuador' | 'sensores') => {
    if (!id) return;
    
    try {
      if (type === 'actuador') {
        setIsDownloadingActuador(true);
        await apiService.downloadActuadorFile(id);
      } else {
        setIsDownloadingSensores(true);
        await apiService.downloadSensoresFile(id);
      }
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.downloadError', 'Error al descargar el archivo'));
    } finally {
      setIsDownloadingActuador(false);
      setIsDownloadingSensores(false);
    }
  };

  const handleDeleteClick = (type: 'actuador' | 'sensores') => {
    setFileToDelete(type);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!id || !ensayo || !fileToDelete) return;
    
    try {
      if (fileToDelete === 'actuador') {
        setIsDeletingActuador(true);
        const updated = await apiService.deleteActuadorFile(id);
        setEnsayo(updated);
        showSuccess(t('ensayos.actuadorDeleteSuccess', 'Archivo de actuador eliminado correctamente'));
      } else {
        setIsDeletingSensores(true);
        const updated = await apiService.deleteSensoresFile(id);
        setEnsayo(updated);
        setSensores([]);
        setDatosPorSensor({});
        showSuccess(t('ensayos.sensoresDeleteSuccess', 'Archivo de sensores eliminado correctamente'));
      }
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.deleteError', 'Error al eliminar el archivo'));
    } finally {
      setIsDeletingActuador(false);
      setIsDeletingSensores(false);
      setDeleteDialogOpen(false);
      setFileToDelete(null);
    }
  };

  const handleStartEdit = (field: 'nombre' | 'codigo' | 'fecha' | 'objetivos' | 'incidencias' | 'conclusiones') => {
    if (!ensayo) return;
    if (field === 'nombre') {
      setIsEditingNombre(true);
      setEditNombre(ensayo.nombre || '');
    } else if (field === 'codigo') {
      setIsEditingCodigo(true);
      setEditCodigo(ensayo.codigo || '');
    } else if (field === 'fecha') {
      setIsEditingFecha(true);
      // Convertir fecha a formato YYYY-MM-DD para el input date
      const fecha = new Date(ensayo.fecha_ensayo);
      const year = fecha.getFullYear();
      const month = String(fecha.getMonth() + 1).padStart(2, '0');
      const day = String(fecha.getDate()).padStart(2, '0');
      setEditFecha(`${year}-${month}-${day}`);
    } else if (field === 'objetivos' || field === 'incidencias' || field === 'conclusiones') {
      // Abrir modal para editar texto
      setTextEditField(field);
      setTextEditValue(ensayo[field] || '');
      setTextEditModalOpen(true);
    }
  };

  const handleOpenTextEditModal = (field: 'objetivos' | 'incidencias' | 'conclusiones') => {
    if (!ensayo || !canManage) return;
    setTextEditField(field);
    setTextEditValue(ensayo[field] || '');
    setTextEditModalOpen(true);
  };

  const handleCloseTextEditModal = () => {
    setTextEditModalOpen(false);
    setTextEditField(null);
    setTextEditValue('');
  };

  const handleSaveTextEdit = async () => {
    if (!id || !ensayo || !textEditField) return;
    
    setIsSaving(true);
    try {
      const updateData: any = {};
      updateData[textEditField] = textEditValue || null;
      
      const updated = await apiService.updateEnsayo(id, updateData);
      setEnsayo(updated);
      showSuccess(t('ensayos.updateSuccess', 'Ensayo actualizado correctamente'));
      handleCloseTextEditModal();
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.updateError', 'Error al actualizar el ensayo'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = (field: 'nombre' | 'codigo' | 'fecha' | 'objetivos' | 'incidencias' | 'conclusiones') => {
    if (!ensayo) return;
    if (field === 'nombre') {
      setIsEditingNombre(false);
      setEditNombre(ensayo.nombre || '');
    } else if (field === 'codigo') {
      setIsEditingCodigo(false);
      setEditCodigo(ensayo.codigo || '');
    } else if (field === 'fecha') {
      setIsEditingFecha(false);
      setEditFecha(ensayo.fecha_ensayo || '');
    } else if (field === 'objetivos') {
      setIsEditingObjetivos(false);
      setEditObjetivos(ensayo.objetivos || '');
    } else if (field === 'incidencias') {
      setIsEditingIncidencias(false);
      setEditIncidencias(ensayo.incidencias || '');
    } else if (field === 'conclusiones') {
      setIsEditingConclusiones(false);
      setEditConclusiones(ensayo.conclusiones || '');
    }
  };

  const handleSaveEdit = async (field: 'nombre' | 'codigo' | 'fecha' | 'objetivos' | 'incidencias' | 'conclusiones') => {
    if (!id || !ensayo) return;
    
    setIsSaving(true);
    try {
      const updateData: Partial<Ensayo> = {};
      if (field === 'nombre') {
        updateData.nombre = editNombre.trim();
      } else if (field === 'codigo') {
        updateData.codigo = editCodigo.trim();
      } else if (field === 'fecha') {
        updateData.fecha_ensayo = editFecha;
      } else if (field === 'objetivos') {
        updateData.objetivos = editObjetivos;
      } else if (field === 'incidencias') {
        updateData.incidencias = editIncidencias;
      } else if (field === 'conclusiones') {
        updateData.conclusiones = editConclusiones;
      }
      
      const updated = await apiService.updateEnsayo(id, updateData);
      setEnsayo(updated);
      
      if (field === 'nombre') {
        setIsEditingNombre(false);
      } else if (field === 'codigo') {
        setIsEditingCodigo(false);
      } else if (field === 'fecha') {
        setIsEditingFecha(false);
      } else if (field === 'objetivos') {
        setIsEditingObjetivos(false);
      } else if (field === 'incidencias') {
        setIsEditingIncidencias(false);
      } else if (field === 'conclusiones') {
        setIsEditingConclusiones(false);
      }
      
      showSuccess(t('ensayos.updateSuccess', 'Campo actualizado correctamente'));
    } catch (err: any) {
      showError(err.response?.data?.detail || t('ensayos.updateError', 'Error al actualizar el campo'));
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (isoDate: string) => {
    if (!isoDate) return '—';
    const parsed = new Date(isoDate);
    return parsed.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (!ensayo) {
    return (
      <Layout>
        <Box>
          <Alert severity="error">{t('ensayos.notFound', 'Ensayo no encontrado')}</Alert>
        </Box>
      </Layout>
    );
  }

  const hasActuador = Boolean(ensayo.actuador_file);
  const hasSensores = Boolean(ensayo.sensores_file);
  const hasAllFiles = hasActuador && hasSensores;

  // Función para manejar cambio de pestaña
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    // Prevenir cambio si la pestaña no está disponible
    const tabKeys = ['info', 'files', 'sensores', 'visualizador'] as const;
    const tabKey = tabKeys[newValue];
    if (tabKey && !tabValidation[tabKey].available) {
      return;
    }
    setActiveTab(newValue);
  };

  // Función para renderizar el contenido de cada pestaña
  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderInfoTab();
      case 1:
        return renderFilesTab();
      case 2:
        return renderSensoresTab();
      case 3:
        return renderVisualizadorTab();
      default:
        return null;
    }
  };

  // Renderizar pestaña de Información del Ensayo
  const renderInfoTab = () => {
    const allMedia = [...galleryMedia.images, ...galleryMedia.videos];
    const currentMedia = allMedia[selectedMediaIndex];

  return (
      <Grid container spacing={3} sx={{ height: '100%', m: 0, width: '100%', maxWidth: '100%' }}>
        {/* Columna izquierda: 2/3 del ancho - Información */}
        <Grid item xs={12} md={8} sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
          <Stack spacing={3} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 2, width: '100%', maxWidth: '100%' }}>
                  <Box>
                    <Typography variant="h6" color="text.primary" display="block" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('ensayos.fields.objetivos', 'Objetivos del ensayo')}
                    </Typography>
                      <Box
                        onClick={() => handleOpenTextEditModal('objetivos')}
                        sx={{ 
              p: 2.5,
                          bgcolor: 'background.default',
              borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: canManage ? 'pointer' : 'default',
                          '&:hover': canManage ? {
                            bgcolor: 'action.hover',
                borderColor: 'primary.main',
                boxShadow: '0 2px 8px rgba(11, 61, 145, 0.1)',
                transform: 'translateY(-2px)',
                          } : {},
              transition: 'all 0.3s ease',
                          minHeight: '100px',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ 
                            wordBreak: 'break-word',
                            fontSize: '0.85rem',
                            lineHeight: 1.7,
                            '& p': { margin: 0, marginBottom: '0.5rem' },
                            '& p:last-child': { marginBottom: 0 },
                            '& ul, & ol': { 
                              margin: 0, 
                              paddingLeft: '1.5rem',
                              paddingTop: '0.5rem',
                              paddingBottom: '0.5rem',
                            },
                            '& li': {
                              marginBottom: '0.25rem',
                            }
                          }}
                          dangerouslySetInnerHTML={{ __html: ensayo.objetivos || t('ensayos.noData', 'Sin datos') }}
                        />
                      </Box>
                  </Box>
                  <Box>
                    <Typography variant="h6" color="text.primary" display="block" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('ensayos.fields.incidencias', 'Incidencias')}
                    </Typography>
                      <Box
                        onClick={() => handleOpenTextEditModal('incidencias')}
                        sx={{ 
              p: 2.5,
                          bgcolor: 'background.default',
              borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: canManage ? 'pointer' : 'default',
                          '&:hover': canManage ? {
                            bgcolor: 'action.hover',
                borderColor: 'primary.main',
                boxShadow: '0 2px 8px rgba(11, 61, 145, 0.1)',
                transform: 'translateY(-2px)',
                          } : {},
              transition: 'all 0.3s ease',
                          minHeight: '80px',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ 
                            wordBreak: 'break-word',
                            fontSize: '0.85rem',
                            lineHeight: 1.7,
                            '& p': { margin: 0, marginBottom: '0.5rem' },
                            '& p:last-child': { marginBottom: 0 },
                            '& ul, & ol': { 
                              margin: 0, 
                              paddingLeft: '1.5rem',
                              paddingTop: '0.5rem',
                              paddingBottom: '0.5rem',
                            },
                            '& li': {
                              marginBottom: '0.25rem',
                            }
                          }}
                          dangerouslySetInnerHTML={{ __html: ensayo.incidencias || t('ensayos.noData', 'Sin datos') }}
                        />
                      </Box>
                  </Box>
                  <Box>
                    <Typography variant="h6" color="text.primary" display="block" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('ensayos.fields.conclusiones', 'Conclusiones')}
                    </Typography>
                      <Box
                        onClick={() => handleOpenTextEditModal('conclusiones')}
                        sx={{ 
              p: 2.5,
                          bgcolor: 'background.default',
              borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: canManage ? 'pointer' : 'default',
                          '&:hover': canManage ? {
                            bgcolor: 'action.hover',
                borderColor: 'primary.main',
                boxShadow: '0 2px 8px rgba(11, 61, 145, 0.1)',
                transform: 'translateY(-2px)',
                          } : {},
              transition: 'all 0.3s ease',
                          minHeight: '80px',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ 
                            wordBreak: 'break-word',
                            fontSize: '0.95rem',
                            lineHeight: 1.7,
                            '& p': { margin: 0, marginBottom: '0.5rem' },
                            '& p:last-child': { marginBottom: 0 },
                            '& ul, & ol': { 
                              margin: 0, 
                              paddingLeft: '1.5rem',
                              paddingTop: '0.5rem',
                              paddingBottom: '0.5rem',
                            },
                            '& li': {
                              marginBottom: '0.25rem',
                            }
                          }}
                          dangerouslySetInnerHTML={{ __html: ensayo.conclusiones || t('ensayos.noData', 'Sin datos') }}
                        />
                      </Box>
                  </Box>
      
                </Stack>
          </Grid>

        {/* Columna derecha: 1/3 del ancho - Galería */}
        <Grid item xs={12} md={4} sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
          <Paper 
            elevation={1}
            sx={{ 
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: 'primary.main',
                }}
              >
                <PhotoLibrary />
                {t('ensayos.gallery', 'Galería')}
                </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {allMedia.length > 0 && (
                  <Tooltip title={t('ensayos.downloadAllMedia', 'Descargar todos los archivos')}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleDownloadAllMedia}
                        disabled={isDownloadingAllMedia}
                        sx={{
                          bgcolor: 'secondary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'secondary.dark',
                          },
                        }}
                      >
                        {isDownloadingAllMedia ? (
                          <CircularProgress size={20} sx={{ color: 'white' }} />
                        ) : (
                          <Download />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {canManage && (
                  <Tooltip title={t('ensayos.uploadMedia', 'Subir imágenes o videos')}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleMediaSelect}
                        disabled={isUploadingMedia}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                        }}
                      >
                        {isUploadingMedia ? (
                          <CircularProgress size={20} sx={{ color: 'white' }} />
                        ) : (
                          <AddPhotoAlternate />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {isLoadingMedia ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
              </Box>
            ) : allMedia.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PhotoLibrary sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('ensayos.gallery.empty', 'No hay fotos o videos en la galería')}
                </Typography>
              </Box>
            ) : (
              <Box 
                sx={{ 
                  overflowY: 'auto', 
                  flex: 1,
                  minHeight: 0,
                  pb: 2,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    bgcolor: 'background.default',
                    borderRadius: 1,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'primary.main',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                }}
              >
                <ImageList cols={2} gap={12} sx={{ m: 0, pb: 1 }}>
                  {galleryMedia.images.map((image) => {
                    const isEditing = editingDescription === image.id;
                    return (
                      <ImageListItem 
                        key={image.id} 
                        sx={{ 
                          overflow: 'visible',
                          borderRadius: 2,
                          position: 'relative',
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            borderRadius: 2,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            '&:hover .delete-button': {
                              opacity: 1,
                            },
                          }}
                          onClick={() => handleOpenMedia(image, 'image')}
                        >
                          <Box
                            component="img"
                            src={mediaBlobUrls[image.id] || ''}
                            alt={image.file_name}
                            sx={{
                              width: '100%',
                              height: 120,
                              objectFit: 'cover',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.05)',
                              },
                            }}
                          />
                          {canManage && (
                            <IconButton
                              className="delete-button"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRequestDeleteMedia(image.id);
                              }}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                opacity: 0,
                                transition: 'opacity 0.3s ease',
                                '&:hover': {
                                  bgcolor: 'error.main',
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </ImageListItem>
                    );
                  })}
                  {galleryMedia.videos.map((video) => {
                    const isEditing = editingDescription === video.id;
                    const videoUrl = mediaBlobUrls[video.id] || '';
                    const thumbnail = videoThumbnails[video.id];
                    
                    // Generar thumbnail si no existe
                    if (!thumbnail && videoUrl) {
                      setTimeout(() => generateVideoThumbnail(videoUrl, video.id), 100);
                    }
                    
                    return (
                      <ImageListItem 
                        key={video.id}
                        sx={{ 
                          overflow: 'visible',
                          borderRadius: 2,
                          position: 'relative',
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            borderRadius: 2,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            '&:hover .delete-button': {
                              opacity: 1,
                            },
                            '&:hover .play-overlay': {
                              opacity: 1,
                            },
                          }}
                          onClick={() => handleOpenMedia(video, 'video')}
                        >
                          {thumbnail ? (
                            <Box
                              component="img"
                              src={thumbnail}
                              alt={video.file_name}
                              sx={{
                                width: '100%',
                                height: 120,
                                objectFit: 'cover',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                },
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: '100%',
                                height: 120,
                                bgcolor: 'background.default',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                              }}
                            >
                              <VideoLibrary 
                                sx={{ 
                                  fontSize: 32, 
                                  color: 'primary.main',
                                }} 
                              />
                            </Box>
                          )}
                          {/* Overlay con icono de play */}
                          <Box
                            className="play-overlay"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'rgba(0, 0, 0, 0.3)',
                              opacity: 0,
                              transition: 'opacity 0.3s ease',
                            }}
                          >
                            <PlayArrow 
                              sx={{ 
                                fontSize: 48,
                                color: 'white',
                              }} 
                            />
                          </Box>
                          {/* Badge de video */}
                          <Chip
                            label="VIDEO"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              bgcolor: 'primary.main',
                              color: 'white',
                              fontSize: '0.65rem',
                              height: 20,
                              fontWeight: 600,
                            }}
                          />
                          {canManage && (
                            <IconButton
                              className="delete-button"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRequestDeleteMedia(video.id);
                              }}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                opacity: 0,
                                transition: 'opacity 0.3s ease',
                                '&:hover': {
                                  bgcolor: 'error.main',
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </ImageListItem>
                    );
                  })}
                </ImageList>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // Renderizar pestaña de Archivos
  const renderFilesTab = () => {
    const sensoresFormatGuess = inferSensoresFileFormat(sensores);
    return (
                <Stack spacing={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                  {hasAllFiles ? (
                    <Alert severity="success" icon={<CheckCircle />}>
                      {t('ensayos.detail.allFilesUploaded', 'Todos los archivos están subidos')}
                    </Alert>
                  ) : (
                    <Alert severity="warning" icon={<ErrorOutline />}>
                      {t('ensayos.detail.missingFiles', 'Faltan archivos por subir')}
                    </Alert>
                  )}

                  <ArchivosSensoresHint />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {t('ensayos.detail.actuador', 'Archivo Actuador')}
                            </Typography>
                            {hasActuador ? (
                              ensayo.actuador_processing_status === 'PENDING' ? (
                                <Chip
                                  icon={<CircularProgress size={16} />}
                                  label={t('ensayos.detail.processing', 'Procesando...')}
                                  color="info"
                                  size="small"
                                />
                              ) : ensayo.actuador_processing_status === 'ERROR' ? (
                                <Chip
                                  icon={<ErrorOutline />}
                                  label={t('ensayos.detail.error', 'Error')}
                                  color="error"
                                  size="small"
                                />
                              ) : (
                                <Chip
                                  icon={<CheckCircle />}
                                  label={t('ensayos.detail.uploaded', 'Subido')}
                                  color="success"
                                  size="small"
                                />
                              )
                            ) : (
                              <Chip
                                icon={<ErrorOutline />}
                                label={t('ensayos.detail.missing', 'Falta')}
                                color="warning"
                                size="small"
                              />
                            )}
                          </Box>
                          {canEditFiles && !hasActuador && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<CloudUpload />}
                              onClick={() => handleFileSelect('actuador')}
                              disabled={isUploadingActuador}
                              fullWidth
                              sx={{ mt: 1 }}
                            >
                              {isUploadingActuador ? (
                                <CircularProgress size={16} />
                              ) : (
                                t('ensayos.detail.upload', 'Subir archivo')
                              )}
                            </Button>
                          )}
                          {hasActuador && (
                            <Box mt={1}>
                              <Box display="flex" alignItems="center" mb={1}>
                                <Description fontSize="small" color="action" sx={{ mr: 1 }} />
                                <Typography 
                                  variant="body2" 
                                  color="text.primary"
                                  sx={{ 
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={ensayo.actuador_file_name || 'Archivo'}
                                >
                                  {ensayo.actuador_file_name || t('ensayos.detail.fileUploaded', 'Archivo disponible')}
                                </Typography>
                              </Box>
                              <Box display="flex" gap={1}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<Download />}
                                  onClick={() => handleDownload('actuador')}
                                  disabled={isDownloadingActuador}
                                  sx={{ flex: 1 }}
                                >
                                  {isDownloadingActuador ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    t('ensayos.detail.download', 'Descargar')
                                  )}
                                </Button>
                                {canEditFiles && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteClick('actuador')}
                                    disabled={isDeletingActuador}
                                    color="error"
                                  >
                                    {isDeletingActuador ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <Delete fontSize="small" />
                                    )}
                                  </IconButton>
                                )}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {t('ensayos.detail.sensores', 'Archivo Sensores')}
                            </Typography>
                            {hasSensores ? (
                              ensayo.sensores_processing_status === 'PENDING' ? (
                                <Chip
                                  icon={<CircularProgress size={16} />}
                                  label={t('ensayos.detail.processing', 'Procesando...')}
                                  color="info"
                                  size="small"
                                />
                              ) : ensayo.sensores_processing_status === 'ERROR' ? (
                                <Chip
                                  icon={<ErrorOutline />}
                                  label={t('ensayos.detail.error', 'Error')}
                                  color="error"
                                  size="small"
                                />
                              ) : (
                                <Chip
                                  icon={<CheckCircle />}
                                  label={t('ensayos.detail.uploaded', 'Subido')}
                                  color="success"
                                  size="small"
                                />
                              )
                            ) : (
                              <Chip
                                icon={<ErrorOutline />}
                                label={t('ensayos.detail.missing', 'Falta')}
                                color="warning"
                                size="small"
                              />
                            )}
                          </Box>
                          {hasSensores && sensores.length > 0 && sensoresFormatGuess !== 'unknown' && (
                            <Box mb={1}>
                              <Chip
                                size="small"
                                variant="outlined"
                                color={sensoresFormatGuess === 'precalc' ? 'secondary' : 'primary'}
                                label={
                                  sensoresFormatGuess === 'precalc'
                                    ? t(
                                        'ensayos.archivos.formatPrecalc',
                                        'Detectado: CATMAN_PRECALC (exportación resampleada/precalculada)'
                                      )
                                    : t('ensayos.archivos.formatRaw', 'Detectado: CATMAN_RAW (datos brutos)')
                                }
                              />
                            </Box>
                          )}
                          {canEditFiles && !hasSensores && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<CloudUpload />}
                              onClick={() => handleFileSelect('sensores')}
                              disabled={isUploadingSensores}
                              fullWidth
                              sx={{ mt: 1 }}
                            >
                              {isUploadingSensores ? (
                                <CircularProgress size={16} />
                              ) : (
                                t('ensayos.detail.upload', 'Subir archivo')
                              )}
                            </Button>
                          )}
                          {hasSensores && (
                            <Box mt={1}>
                              <Box display="flex" alignItems="center" mb={1}>
                                <Description fontSize="small" color="action" sx={{ mr: 1 }} />
                                <Typography 
                                  variant="body2" 
                                  color="text.primary"
                                  sx={{ 
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={ensayo.sensores_file_name || 'Archivo'}
                                >
                                  {ensayo.sensores_file_name || t('ensayos.detail.fileUploaded', 'Archivo disponible')}
                                </Typography>
                              </Box>
                              <Box display="flex" gap={1}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<Download />}
                                  onClick={() => handleDownload('sensores')}
                                  disabled={isDownloadingSensores}
                                  sx={{ flex: 1 }}
                                >
                                  {isDownloadingSensores ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    t('ensayos.detail.download', 'Descargar')
                                  )}
                                </Button>
                                {canEditFiles && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteClick('sensores')}
                                    disabled={isDeletingSensores}
                                    color="error"
                                  >
                                    {isDeletingSensores ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <Delete fontSize="small" />
                                    )}
                                  </IconButton>
                                )}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <NomenclaturaDocLink />
                </Stack>
    );
  };

  // Renderizar pestaña de Visualizador
  const renderVisualizadorTab = () => {
    // Verificar si hay datos disponibles (actuador o sensores)
    const hasData = actuadorData.length > 0 || sensores.length > 0;
    const hasAnyDataFile = ensayo?.actuador_file || ensayo?.sensores_file;

    return (
      <Stack spacing={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!hasAnyDataFile && (
          <Alert severity="info">
            {t('ensayos.noDataProcessed', 'No hay archivos de datos. Sube archivos de actuador o sensores en la pestaña Archivos para visualizar los datos.')}
          </Alert>
        )}
        {hasAnyDataFile && !hasData && !isLoadingActuadorData && !isLoadingSensores && (
          <Alert 
            severity="info" 
            action={
              canEditFiles && (
                <CorporateButton
                  size="small"
                  onClick={() => {
                    if (ensayo.actuador_file && actuadorData.length === 0) {
                      handleProcessActuador();
                    }
                    if (ensayo.sensores_file && sensores.length === 0) {
                      handleProcessSensores();
                    }
                  }}
                  disabled={isProcessingActuador || isProcessingSensores}
                  startIcon={(isProcessingActuador || isProcessingSensores) ? <CircularProgress size={16} /> : undefined}
                >
                  {(isProcessingActuador || isProcessingSensores)
                    ? t('ensayos.processing', 'Procesando...')
                    : t('ensayos.processFile', 'Procesar archivo')}
                </CorporateButton>
              )
            }
          >
            {t('ensayos.noDataProcessed', 'No hay datos procesados. Procesa el archivo para visualizar los datos.')}
          </Alert>
        )}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <VisualizadorDatosEnsayo
            ensayoId={id || ''}
            actuadorData={actuadorData}
            actuadorDataTotal={actuadorDataTotal}
            sensores={sensores}
            datosPorSensor={datosPorSensor}
            isLoading={isLoadingActuadorData || isLoadingSensores}
            error={actuadorDataError || sensoresError}
            onLoadSensorData={handleLoadSensorData}
            onLoadSensorsBatch={handleLoadSensorsBatch}
            onMovingAverageChange={handleMovingAverageChange}
          />
        </Box>
      </Stack>
    );
  };

  // Renderizar pestaña de Sensores
  const renderSensoresTab = () => (
    <Stack spacing={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="flex-end">
            <NomenclaturaDocLink compact />
          </Box>
          {/* Metadatos de sensores */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('ensayos.detail.sensorsMetadata', 'Metadatos de Sensores')}
                    </Typography>
                    {sensores.length === 0 && !isLoadingSensores && (
                      <Alert 
                        severity="info" 
                        action={
                          canEditFiles && (
                            <CorporateButton
                              size="small"
                              onClick={handleProcessSensores}
                              disabled={isProcessingSensores}
                              startIcon={isProcessingSensores ? <CircularProgress size={16} /> : undefined}
                            >
                              {isProcessingSensores 
                                ? t('ensayos.processing', 'Procesando...')
                                : t('ensayos.processFile', 'Procesar archivo')}
                            </CorporateButton>
                          )
                        }
                      >
                        {t('ensayos.noSensorsProcessed', 'No hay sensores procesados. Procesa el archivo para ver los metadatos.')}
                      </Alert>
                    )}
                    {sensoresError && (
                      <Alert severity="error">
                        {sensoresError}
                      </Alert>
                    )}
                    <SensorMetadata
                      sensores={sensores}
                      isLoading={isLoadingSensores}
                      ensayoId={id || ''}
                      onSensoresUpdate={(silent) => {
                        loadSensores(silent);
                        loadEnsayo(false);
                      }}
                      datosPorSensor={datosPorSensor}
                      onReloadSensorData={handleLoadSensorData}
                      displacementStatus={ensayo?.displacement_calculation_status}
                      fosStatus={ensayo?.fos_strain_calculation_status}
                      ensayoCodigo={ensayo?.codigo}
                    />
      </Box>
                  </Stack>
  );

  return (
    <Layout>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: 'calc(100vh - 57.6px - 80px)', // TopBar (57.6px) + padding top/bottom (40px * 2)
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
      }}>
        <Box sx={{ flexShrink: 0, width: '100%', mb: 1.5 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <IconButton
              onClick={() => navigate('/ensayos')}
              size="small"
              sx={{
                mr: -1,
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: 'primary.main',
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            {isEditingNombre ? (
              <Box display="flex" alignItems="center" gap={1} flex={1} minWidth="300px">
                <CorporateTextField
                  value={editNombre}
                  onChange={(e) => setEditNombre(e.target.value)}
                  fullWidth
                  autoFocus
                  size="small"
                />
                <IconButton 
                  size="small" 
                  onClick={() => handleCancelEdit('nombre')}
                  disabled={isSaving}
                >
                  <Cancel fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={() => handleSaveEdit('nombre')}
                  disabled={isSaving}
                  color="primary"
                >
                  {isSaving ? <CircularProgress size={16} /> : <Save fontSize="small" />}
                </IconButton>
              </Box>
            ) : (
              <CorporateTitle 
                level="h3"
                onClick={() => canManage && handleStartEdit('nombre')}
                sx={{ 
                  cursor: canManage ? 'pointer' : 'default',
                  '&:hover': canManage ? {
                    opacity: 0.7
                  } : {}
                }}
              >
                {ensayo.nombre}
              </CorporateTitle>
            )}
            <Box display="flex" alignItems="center" gap={1}>
              {isEditingCodigo ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CorporateTextField
                    value={editCodigo}
                    onChange={(e) => setEditCodigo(e.target.value)}
                    size="small"
                    sx={{ width: '150px' }}
                    autoFocus
                  />
                  <IconButton 
                    size="small" 
                    onClick={() => handleCancelEdit('codigo')}
                    disabled={isSaving}
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleSaveEdit('codigo')}
                    disabled={isSaving}
                    color="primary"
                  >
                    {isSaving ? <CircularProgress size={16} /> : <Save fontSize="small" />}
                  </IconButton>
                </Box>
              ) : (
                <>
                  <Chip 
                    label={ensayo.codigo} 
                    color="primary"
                    onClick={() => canManage && handleStartEdit('codigo')}
                    sx={{ 
                      cursor: canManage ? 'pointer' : 'default',
                      '&:hover': canManage ? {
                        opacity: 0.8
                      } : {}
                    }}
                  />
                  {ensayo.proyecto?.codigo_grant && (
                    <Chip 
                      label={ensayo.proyecto.codigo_grant} 
                      color="secondary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {ensayo.tipo_ensayo && (
                    <Chip 
                      label={ensayo.tipo_ensayo === '3 Puntos' ? '3P' : ensayo.tipo_ensayo === '4 Puntos' ? '4P' : ensayo.tipo_ensayo} 
                      color={ensayo.tipo_ensayo === '3 Puntos' ? 'info' : 'warning'}
                      size="small"
                      title={ensayo.tipo_ensayo}
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </>
              )}
            </Box>
            {isEditingFecha ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CorporateTextField
                  type="date"
                  value={editFecha}
                  onChange={(e) => setEditFecha(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  autoFocus
                />
                <IconButton 
                  size="small" 
                  onClick={() => handleCancelEdit('fecha')}
                  disabled={isSaving}
                >
                  <Cancel fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={() => handleSaveEdit('fecha')}
                  disabled={isSaving}
                  color="primary"
                >
                  {isSaving ? <CircularProgress size={16} /> : <Save fontSize="small" />}
                </IconButton>
              </Box>
            ) : (
              <Typography 
                color="text.secondary" 
                variant="body2"
                onClick={() => canManage && handleStartEdit('fecha')}
                sx={{ 
                  cursor: canManage ? 'pointer' : 'default',
                  '&:hover': canManage ? {
                    opacity: 0.7,
                    textDecoration: 'underline'
                  } : {}
                }}
              >
                {formatDate(ensayo.fecha_ensayo)}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Sistema de Pestañas */}
        <Paper 
          elevation={2}
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 4px 12px rgba(11, 61, 145, 0.12)',
            transition: 'box-shadow 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            width: '100%',
            maxWidth: '100%',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(11, 61, 145, 0.15)',
            },
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: '2px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, #0B3D91 0%, #00B5A6 100%)',
                opacity: 0.1,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                minHeight: 48,
                px: 2.5,
                py: 1.25,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                borderRadius: '8px 8px 0 0',
                margin: '0 4px',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: '3px',
                  bgcolor: 'primary.main',
                  transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                },
                '&:hover:not(.Mui-disabled)': {
                  bgcolor: 'action.hover',
                  '&::before': {
                    width: '60%',
                    bgcolor: 'primary.light',
                  },
                },
                '&.Mui-selected': {
                  color: 'primary.main',
                  bgcolor: 'rgba(11, 61, 145, 0.04)',
                  '&::before': {
                    width: '80%',
                    bgcolor: 'primary.main',
                  },
                },
                '&.Mui-disabled': {
                  opacity: 0.4,
                  cursor: 'not-allowed',
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: 'linear-gradient(90deg, #0B3D91 0%, #00B5A6 100%)',
                boxShadow: '0 2px 8px rgba(11, 61, 145, 0.3)',
              },
            }}
          >
            <Tooltip
              title={
                tabValidation.info.hasError
                  ? t('ensayos.tabs.infoError', 'Completa la información del ensayo para continuar')
                  : ''
              }
              arrow
              placement="top"
              disableInteractive
            >
              <Tab
                icon={<Info />}
                iconPosition="start"
                label={t('ensayos.detail.info', 'Información del Ensayo')}
                sx={{
                  color: tabValidation.info.hasError ? 'error.main' : undefined,
                  '&.Mui-selected': {
                    color: tabValidation.info.hasError ? 'error.main' : 'primary.main',
                  },
                  '& .MuiTab-iconWrapper': {
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover:not(.Mui-disabled) .MuiTab-iconWrapper': {
                    transform: 'scale(1.1)',
                  },
                }}
              />
            </Tooltip>
            <Tab
              icon={<Folder />}
              iconPosition="start"
              label={t('ensayos.detail.files', 'Archivos')}
              sx={{
                '& .MuiTab-iconWrapper': {
                  transition: 'transform 0.3s ease',
                },
                '&:hover:not(.Mui-disabled) .MuiTab-iconWrapper': {
                  transform: 'scale(1.1)',
                },
              }}
            />
            <Tooltip
              title={
                !tabValidation.sensores.available
                  ? t('ensayos.tabs.sensoresDisabled', 'Sube el archivo de sensores en la pestaña Archivos para acceder a los sensores')
                  : tabValidation.sensores.hasError
                  ? t('ensayos.tabs.sensoresError', 'Procesa el archivo de sensores para ver los datos')
                  : ''
              }
              arrow
              placement="top"
              disableInteractive
            >
              <TabTooltipWrapper>
                <Tab
                  icon={<Sensors />}
                  iconPosition="start"
                  label={t('ensayos.detail.sensores', 'Sensores')}
                  disabled={!tabValidation.sensores.available}
                  onClick={(e) => {
                    if (tabValidation.sensores.available) {
                      handleTabChange(e, 2);
                    }
                  }}
                  sx={{
                    color: tabValidation.sensores.hasError ? 'error.main' : undefined,
                    '&.Mui-selected': {
                      color: tabValidation.sensores.hasError ? 'error.main' : 'primary.main',
                    },
                    '& .MuiTab-iconWrapper': {
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover:not(.Mui-disabled) .MuiTab-iconWrapper': {
                      transform: 'scale(1.1)',
                    },
                  }}
                />
              </TabTooltipWrapper>
            </Tooltip>
            <Tooltip
              title={
                !tabValidation.visualizador.available
                  ? t('ensayos.tabs.visualizadorDisabled', 'Sube el archivo de actuador o sensores en la pestaña Archivos para acceder al visualizador')
                  : tabValidation.visualizador.hasError
                  ? t('ensayos.tabs.visualizadorError', 'Procesa el archivo de actuador o sensores para visualizar los datos')
                  : ''
              }
              arrow
              placement="top"
              disableInteractive
            >
              <TabTooltipWrapper>
                <Tab
                  icon={<ShowChart />}
                  iconPosition="start"
                  label={t('ensayos.detail.visualizador', 'Visualizador')}
                  disabled={!tabValidation.visualizador.available}
                  onClick={(e) => {
                    if (tabValidation.visualizador.available) {
                      handleTabChange(e, 3);
                    }
                  }}
                  sx={{
                    color: tabValidation.visualizador.hasError ? 'error.main' : undefined,
                    '&.Mui-selected': {
                      color: tabValidation.visualizador.hasError ? 'error.main' : 'primary.main',
                    },
                    '& .MuiTab-iconWrapper': {
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover:not(.Mui-disabled) .MuiTab-iconWrapper': {
                      transform: 'scale(1.1)',
                    },
                  }}
                />
              </TabTooltipWrapper>
            </Tooltip>
          </Tabs>

          {/* Contenido de las pestañas */}
          <Box 
            sx={{ 
              p: 3, 
              bgcolor: 'background.paper',
              position: 'relative',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: '100%',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent 0%, rgba(11, 61, 145, 0.1) 50%, transparent 100%)',
              },
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', width: '100%', maxWidth: '100%' }}>
              {renderTabContent()}
            </Box>
          </Box>
        </Paper>

        {/* Inputs ocultos para subir archivos */}
        <input
          type="file"
          ref={actuadorFileInputRef}
          onChange={(e) => handleFileChange(e, 'actuador')}
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
        />
        <input
          type="file"
          ref={sensoresFileInputRef}
          onChange={(e) => handleFileChange(e, 'sensores')}
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
        />

        {/* Diálogo de confirmación de eliminación */}
        <CorporateDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setFileToDelete(null);
          }}
          title={
            fileToDelete === 'actuador'
              ? t('ensayos.deleteActuador', 'Eliminar archivo de actuador')
              : t('ensayos.deleteSensores', 'Eliminar archivo de sensores')
          }
          icon={<Delete />}
          actions={
            <>
              <CorporateButton
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setFileToDelete(null);
                }}
                color="inherit"
              >
                {t('common.cancel', 'Cancelar')}
              </CorporateButton>
              <CorporateButton
                onClick={handleConfirmDelete}
                color="error"
                disabled={isDeletingActuador || isDeletingSensores}
              >
                {t('common.delete', 'Eliminar')}
              </CorporateButton>
            </>
          }
        >
          <Typography variant="body2">
            {fileToDelete === 'actuador'
              ? t('ensayos.deleteActuadorConfirm', '¿Está seguro de que desea eliminar el archivo de actuador? Esta acción no se puede deshacer.')
              : t('ensayos.deleteSensoresConfirm', '¿Está seguro de que desea eliminar el archivo de sensores? Esta acción no se puede deshacer.')}
          </Typography>
        </CorporateDialog>

        {/* Diálogo de confirmación de eliminación de media */}
        <CorporateDialog
          open={deleteMediaDialogOpen}
          onClose={() => {
            setDeleteMediaDialogOpen(false);
            setMediaToDelete(null);
          }}
          title={t('ensayos.deleteMedia', 'Eliminar archivo multimedia')}
          icon={<Delete />}
          actions={
            <>
              <CorporateButton
                onClick={() => {
                  setDeleteMediaDialogOpen(false);
                  setMediaToDelete(null);
                }}
                color="inherit"
              >
                {t('common.cancel', 'Cancelar')}
              </CorporateButton>
              <CorporateButton
                onClick={handleConfirmDeleteMedia}
                color="error"
                disabled={isDeletingMedia}
                startIcon={isDeletingMedia ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {t('common.delete', 'Eliminar')}
              </CorporateButton>
            </>
          }
        >
          <Typography variant="body2">
            {t('ensayos.deleteMediaConfirm', '¿Está seguro de que desea eliminar este archivo multimedia? Esta acción no se puede deshacer.')}
          </Typography>
        </CorporateDialog>

        {/* Input oculto para subir archivos multimedia (múltiples) */}
        <input
          type="file"
          ref={mediaFileInputRef}
          onChange={handleMediaChange}
          accept="image/*,video/*"
          multiple
          style={{ display: 'none' }}
        />

        {/* Diálogo para visualizar imágenes/videos con navegación */}
        <Dialog
          open={mediaDialogOpen}
          onClose={() => {
            setMediaDialogOpen(false);
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'background.dark',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              position: 'relative',
            },
          }}
        >
          <DialogContent sx={{ p: 0, position: 'relative', minHeight: '70vh' }}>
            {/* Botón cerrar */}
            <IconButton
              onClick={() => {
                setMediaDialogOpen(false);
              }}
              sx={{
                position: 'absolute',
                right: 12,
                top: 12,
                zIndex: 10,
                bgcolor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.8)',
                  transform: 'rotate(90deg)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Close />
            </IconButton>

            {(() => {
              const allMedia = [...galleryMedia.images, ...galleryMedia.videos];
              const currentMedia = allMedia[selectedMediaIndex];
              
              return (
                <>
                  {/* Botón descargar */}
                  {currentMedia && (
                    <Tooltip title={t('ensayos.downloadMedia', 'Descargar archivo')}>
                      <span>
                        <IconButton
                          onClick={() => handleDownloadMedia(currentMedia)}
                          disabled={isDownloadingMedia}
                          sx={{
                            position: 'absolute',
                            right: 12,
                            top: 60,
                            zIndex: 10,
                            bgcolor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.8)',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {isDownloadingMedia ? (
                            <CircularProgress size={20} sx={{ color: 'white' }} />
                          ) : (
                            <Download />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Botones de navegación (solo si hay más de un elemento) */}
                  {allMedia.length > 1 && (
                    <>
                      <IconButton
                        onClick={handlePreviousMedia}
                        sx={{
                          position: 'absolute',
                          left: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          bgcolor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <ArrowBack />
                      </IconButton>
                      <IconButton
                        onClick={handleNextMedia}
                        sx={{
                          position: 'absolute',
                          right: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          bgcolor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <ArrowForward />
                      </IconButton>
                    </>
                  )}

                  {/* Contador de imágenes/videos */}
                  {allMedia.length > 1 && currentMedia && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10,
                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="body2">
                        {selectedMediaIndex + 1} / {allMedia.length}
                      </Typography>
                    </Box>
                  )}

                  {/* Contenido del media */}
                  {currentMedia && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        minHeight: '70vh',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flex: 1,
                          p: 2,
                          pb: 0,
                          overflow: 'auto',
                        }}
                      >
                        {selectedMediaType === 'image' ? (
                          <Box
                            component="img"
                            src={mediaBlobUrls[currentMedia.id] || ''}
                            alt={currentMedia.file_name}
                            sx={{
                              maxWidth: '100%',
                              maxHeight: 'calc(85vh - 120px)',
                              height: 'auto',
                              objectFit: 'contain',
                              borderRadius: 2,
                            }}
                          />
                        ) : (
                          <Box
                            component="video"
                            src={mediaBlobUrls[currentMedia.id] || ''}
                            controls
                            autoPlay
                            sx={{
                              maxWidth: '100%',
                              maxHeight: 'calc(85vh - 120px)',
                              width: '100%',
                              borderRadius: 2,
                            }}
                          />
                        )}
                      </Box>
                      
                      {/* Descripción en el modal */}
                      <Box
                        sx={{
                          bgcolor: 'rgba(0, 0, 0, 0.8)',
                          p: 2,
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                          flexShrink: 0,
                        }}
                      >
                        {editingDescription === currentMedia.id ? (
                          <Box>
                            <CorporateTextField
                              value={editDescriptionValue}
                              onChange={(e) => setEditDescriptionValue(e.target.value)}
                              placeholder={t('ensayos.gallery.descriptionPlaceholder', 'Añadir descripción...')}
                              fullWidth
                              multiline
                              rows={3}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                  e.preventDefault();
                                  handleSaveDescription(currentMedia.id);
                                }
                                if (e.key === 'Escape') {
                                  handleCancelEditDescription();
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                                  color: 'white',
                                  '& fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.7)',
                                  },
                                },
                                '& .MuiInputBase-input': {
                                  color: 'white',
                                  '&::placeholder': {
                                    color: 'rgba(255, 255, 255, 0.5)',
                                  },
                                },
                              }}
                            />
                            <Box display="flex" gap={1} mt={1.5} justifyContent="flex-end">
                              <IconButton
                                size="small"
                                onClick={handleCancelEditDescription}
                                sx={{
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                                  },
                                }}
                              >
                                <Cancel />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleSaveDescription(currentMedia.id)}
                                sx={{
                                  color: 'white',
                                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                                  '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                                  },
                                }}
                              >
                                <Save />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 2,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'white',
                                  fontSize: '0.9rem',
                                  lineHeight: 1.6,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {currentMedia.description || (
                                  <Box component="span" sx={{ fontStyle: 'italic', opacity: 0.6 }}>
                                    {canManage 
                                      ? t('ensayos.gallery.clickToAddDescription', 'Clic para añadir descripción')
                                      : t('ensayos.gallery.noDescription', 'Sin descripción')
                                    }
                                  </Box>
                                )}
                              </Typography>
                            </Box>
                            {canManage && (
                              <IconButton
                                size="small"
                                onClick={() => handleStartEditDescription(currentMedia.id, currentMedia.description)}
                                sx={{
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                                  },
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Modal para editar Objetivos, Incidencias o Conclusiones */}
        <Dialog
          open={textEditModalOpen}
          onClose={handleCloseTextEditModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            },
          }}
        >
          <DialogContent sx={{ p: 0, position: 'relative' }}>
            {/* Botón cerrar */}
            <IconButton
              onClick={handleCloseTextEditModal}
              sx={{
                position: 'absolute',
                right: 12,
                top: 12,
                zIndex: 10,
                bgcolor: 'rgba(0, 0, 0, 0.05)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.1)',
                  transform: 'rotate(90deg)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Close />
            </IconButton>

            <Box sx={{ p: 4, pt: 5 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                {textEditField === 'objetivos' && t('ensayos.fields.objetivos', 'Objetivos del ensayo')}
                {textEditField === 'incidencias' && t('ensayos.fields.incidencias', 'Incidencias')}
                {textEditField === 'conclusiones' && t('ensayos.fields.conclusiones', 'Conclusiones')}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <CorporateRichTextEditor
                  label={
                    textEditField === 'objetivos' ? t('ensayos.fields.objetivos', 'Objetivos del ensayo') :
                    textEditField === 'incidencias' ? t('ensayos.fields.incidencias', 'Incidencias') :
                    t('ensayos.fields.conclusiones', 'Conclusiones')
                  }
                  value={textEditValue}
                  onChange={(value) => setTextEditValue(value)}
                  minHeight={400}
                />
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <CorporateButton
                  onClick={handleCloseTextEditModal}
                  disabled={isSaving}
                  color="inherit"
                  startIcon={<Cancel />}
                >
                  {t('common.cancel', 'Cancelar')}
                </CorporateButton>
                <CorporateButton
                  onClick={handleSaveTextEdit}
                  disabled={isSaving}
                  startIcon={isSaving ? <CircularProgress size={16} /> : <Save />}
                >
                  {t('common.save', 'Guardar')}
                </CorporateButton>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </Layout>
  );
};
